import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { taskService } from '../api/taskService';
import { eventService } from '../api/eventService';
import { userService } from '../api/userService';
import GanttChart, { getDivisionColor, getPriorityColor } from './GanttChart';
import { useAuth } from '../auth/AuthContext';
import { useLocation } from 'react-router-dom';
import { Calendar as CalendarIcon, Filter, X, ChevronLeft, ChevronRight, Clock, Maximize, Minimize, User } from 'lucide-react';
import { getUserName } from '../utils/userUtils';
import { ViewMode } from 'gantt-task-react';
import { formatDateTimeIST } from '../utils/dateUtils';
import { DIVISIONS } from '../constants/divisions';

// --- Details Modal ---
const ItemDetailsModal = ({ item, users = [], onClose }) => {
    const isTask = item?.entityType === 'TASK';
    const itemId = isTask ? item?.TaskID : item?.EventID;

    const { data: fetchedSubitems = [] } = useQuery({
        queryKey: ['subitems', item?.entityType, itemId],
        queryFn: async () => {
            if (!itemId) return [];
            try {
                if (isTask) {
                    return await taskService.getSubTasks(itemId);
                } else {
                    return await eventService.getSubEvents(itemId);
                }
            } catch (err) {
                console.error("Error fetching subitems", err);
                return [];
            }
        },
        enabled: !!itemId
    });

    if (!item) return null;

    const creatorName = getUserName(item.CreatedBy, users);
    const assigneeName = item.AssigneeID ? getUserName(item.AssigneeID, users) : null;
    const color = isTask ? getDivisionColor(item.Division) : '#f59e0b';

    const finalSubitems = fetchedSubitems.length > 0 ? fetchedSubitems : (item.Subtasks || item.SubEvents || []);

    const getStatusStyle = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'completed') return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
        if (s === 'in progress' || s === 'ongoing') return { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' };
        if (s === 'cancelled') return { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' };
        return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
    };

    const mainStatusStyle = getStatusStyle(item.Status);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100
        }}>
            <div style={{
                background: 'white', padding: '0', borderRadius: '12px', width: '90%', maxWidth: '500px',
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{ padding: '1.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <div style={{
                            display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '4px',
                            background: color, color: 'white', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem'
                        }}>
                            {item.entityType} • {item.Division || 'General'}
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>{item.Title}</h3>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={24} /></button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem', display: 'grid', gap: '1rem', overflowY: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Status</label>
                            <div style={{ marginTop: '0.25rem', color: mainStatusStyle.color, fontWeight: 600, fontSize: '0.9rem' }}>
                                {item.Status || 'Pending'}
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Priority</label>
                            <div style={{ color: getPriorityColor(item.Priority), fontWeight: 600 }}>{item.Priority || 'Normal'}</div>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{isTask ? 'Start Date' : 'Start Time'}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#334155' }}>
                                <Clock size={14} /> {formatDateTimeIST(item.start)}
                            </div>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{isTask ? 'Due Date' : 'End Time'}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#334155' }}>
                                <Clock size={14} /> {formatDateTimeIST(item.end)}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Description</label>
                        <p style={{ margin: '0.5rem 0 0', color: '#475569', fontSize: '0.9rem', lineHeight: '1.5' }}>
                            {item.Description || 'No description provided.'}
                        </p>
                    </div>

                    {/* Team & Assignment Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                        <div>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Created By</label>
                            <p style={{ margin: '0.35rem 0 0', display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#334155', fontWeight: 500, fontSize: '0.85rem' }}>
                                <User size={14} style={{ color: '#64748b' }} />
                                {creatorName}
                            </p>
                        </div>
                        {isTask && (
                            <div>
                                <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Assigned To</label>
                                <p style={{ margin: '0.35rem 0 0', display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#1e40af', fontWeight: 600, fontSize: '0.85rem' }}>
                                    <User size={14} style={{ color: '#3b82f6' }} />
                                    {assigneeName || 'Unassigned'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Extra Event Details */}
                    {!isTask && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                            {item.Category && (
                                <div>
                                    <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Classification</label>
                                    <div style={{ color: '#334155', fontWeight: 500, fontSize: '0.9rem' }}>{item.Category} • {item.SubType}</div>
                                </div>
                            )}
                            {item.Mode && (
                                <div>
                                    <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Mode & Location</label>
                                    <div style={{ color: '#334155', fontWeight: 500, fontSize: '0.9rem' }}>{item.Mode} {item.Location ? `• ${item.Location}` : ''}</div>
                                </div>
                            )}
                            {item.ChiefGuest && (
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Chief Guest</label>
                                    <div style={{ color: '#334155', fontWeight: 500, fontSize: '0.9rem' }}>{item.ChiefGuest}</div>
                                </div>
                            )}
                            {(item.HostOrganisation || item.ExternalOrganisation) && (
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Organizations</label>
                                    <div style={{ color: '#334155', fontWeight: 500, fontSize: '0.9rem' }}>
                                        {item.HostOrganisation && `Host: ${item.HostOrganisation}`}
                                        {item.HostOrganisation && item.ExternalOrganisation && ' | '}
                                        {item.ExternalOrganisation && `External: ${item.ExternalOrganisation}`}
                                    </div>
                                </div>
                            )}
                            {item.Panelists && item.Panelists.length > 0 && (
                                <div style={{ gridColumn: 'span 2', marginTop: '0.5rem' }}>
                                    <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: '0.25rem' }}>Panelists</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                        {item.Panelists.map((panelist, pIndex) => (
                                            <span
                                                key={pIndex}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                                    background: panelist.type === 'internal' ? '#eff6ff' : '#ecfdf5',
                                                    color: panelist.type === 'internal' ? '#1e40af' : '#065f46',
                                                    border: `1px solid ${panelist.type === 'internal' ? '#bfdbfe' : '#a7f3d0'}`,
                                                    fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {panelist.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sub-items (if any) */}
                    {finalSubitems.length > 0 && (
                        <div style={{ marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.75rem', display: 'block' }}>
                                {isTask ? 'Subtasks' : 'Sub-events'}
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {finalSubitems.map((sub, i) => {
                                    const subStyle = getStatusStyle(sub.Status);
                                    const subAssignee = sub.AssigneeID ? getUserName(sub.AssigneeID, users) : null;
                                    return (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.25rem' }}>

                                                    {sub.ActivityNameManual || sub.title || sub.ActivityType || 'Untitled Subtask'}
                                                </div>
                                                {subAssignee && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#64748b', fontSize: '0.75rem' }}>
                                                        <User size={12} /> {subAssignee}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ 
                                                fontSize: '0.75rem', fontWeight: 600,
                                                color: subStyle.color, whiteSpace: 'nowrap'
                                            }}>
                                                {sub.Status || 'Open'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        onClick={onClose}
                        style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', color: '#334155', cursor: 'pointer', fontWeight: 500 }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};


const Calendar = () => {
    const { user } = useAuth();
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE');
    const isCoordinator = user?.roles?.some(r => r.startsWith('COORDINATOR_') || r === 'COORDINATOR_RIS');

    // View State
    const [view, setView] = useState('Month'); // 'Month' | 'Timeline'
    const [isFullScreen, setIsFullScreen] = useState(false);
    const calendarRef = React.useRef(null);

    // Fullscreen Handler
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            calendarRef.current?.requestFullscreen().then(() => setIsFullScreen(true)).catch(err => {
                console.error("Error attempting to enable fullscreen:", err);
            });
        } else {
            document.exitFullscreen().then(() => setIsFullScreen(false));
        }
    };

    // Listen to native exit (e.g., pressing ESC)
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Route Parsing
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const targetDateStr = queryParams.get('date');
    const [activeHighlight, setActiveHighlight] = useState(targetDateStr);

    // Filter States
    const [activeDivision, setActiveDivision] = useState('All');
    const [timeScale, setTimeScale] = useState('Month'); // 'Month' | 'Week' | 'Day' (Only for Timeline)
    const [entityType, setEntityType] = useState(targetDateStr ? 'Events' : 'All'); // 'All' | 'Tasks' | 'Events'

    // Data State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    // Interactions
    const [selectedItem, setSelectedItem] = useState(null); // For Details Modal

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Derived Visual States
    // Map timeScale to Gantt ViewMode
    const ganttViewMode = timeScale === 'Day' ? ViewMode.Day :
        timeScale === 'Week' ? ViewMode.Week :
            ViewMode.Month;

    const { data: usersData = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000,
        enabled: !!user
    });

    const usersList = useMemo(() => Array.isArray(usersData) ? usersData : (usersData?.Users || []), [usersData]);

    const { data: mergedItems = [], isLoading: loadingItems } = useQuery({
        queryKey: ['calendar_items', activeDivision, entityType, isDG, isCoordinator],
        queryFn: async () => {
            let tasksPromise = Promise.resolve([]);
            let eventsPromise = Promise.resolve([]);

            let scopeDivision = null;
            if (isDG) {
                scopeDivision = activeDivision === 'All' ? null : activeDivision;
            } else if (isCoordinator) {
                scopeDivision = user?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_')[1];
            }

            if (entityType !== 'Events') {
                if (scopeDivision) {
                    tasksPromise = taskService.getDivisionTasks(scopeDivision);
                } else if (isDG) {
                    tasksPromise = taskService.getAllTasks();
                } else {
                    tasksPromise = taskService.getAll();
                }
            }

            if (entityType !== 'Tasks') {
                if (scopeDivision) {
                    eventsPromise = eventService.getDivisionEvents(scopeDivision);
                } else if (isDG) {
                    eventsPromise = eventService.getAllEvents();
                } else {
                    eventsPromise = eventService.getAll();
                }
            }

            const [tasksData, eventsData] = await Promise.all([tasksPromise, eventsPromise]);

            const safeDate = (d) => {
                if (!d) return null;
                const parsed = new Date(d);
                return isNaN(parsed.getTime()) ? null : parsed;
            };

            const normalizedTasks = (tasksData || [])
                .filter(t => (t.Status || t.status || '').toLowerCase() !== 'cancelled' && t.ShowOnCalendar !== false && t.showOnCalendar !== false)
                .map(t => {
                    const dateObj = safeDate(t.DueDate) || safeDate(t.EndDate) || safeDate(t.PrevEndDate) || new Date();
                    let startObj = safeDate(t.StartDate) || safeDate(t.CreatedAt) || new Date();
                    let endObj = safeDate(t.EndDate) || safeDate(t.DueDate) || new Date();

                    // Gantt chart fails to render or struggles with 0-duration tasks properly.
                    // If start and end are exactly the same or start is after end, guarantee a 1-day span.
                    if (endObj <= startObj) {
                        endObj = new Date(startObj);
                        endObj.setDate(endObj.getDate() + 1);
                    }

                    return {
                        ...t,
                        entityType: 'TASK',
                        date: dateObj,
                        start: startObj,
                        end: endObj,
                        Title: t.Title || 'Untitled Task',
                        Division: t.Division || t.division
                    };
                });

            const normalizedEvents = (eventsData || [])
                .filter(e => (e.Status || e.status || '').toLowerCase() !== 'cancelled' && e.ShowOnCalendar !== false && e.showOnCalendar !== false)
                .map(e => {
                    const startObj = safeDate(e.StartDateTime) || safeDate(e.StartDate) || new Date();
                    const endObj = safeDate(e.EndDateTime) || safeDate(e.EndDate) || startObj;

                    return {
                        ...e,
                        entityType: 'EVENT',
                        Title: e.Title || e.EventName || 'Untitled Event',
                        date: startObj,
                        start: startObj,
                        end: endObj,
                        Division: e.Division || e.division || 'Other'
                    };
                });


            return [...normalizedTasks, ...normalizedEvents];
        },
        staleTime: 60000,
        enabled: !!user
    });

    useEffect(() => {
        setItems(mergedItems || []);
    }, [mergedItems]);

    useEffect(() => {
        setUsers(usersList);
    }, [usersList]);

    useEffect(() => {
        setLoading(loadingItems || loadingUsers);
    }, [loadingItems, loadingUsers]);

    useEffect(() => {
        if (targetDateStr) {
            const parsed = new Date(targetDateStr);
            if (!isNaN(parsed.getTime())) {
                setCurrentDate(parsed);
                setActiveHighlight(targetDateStr);
                setView('Month'); // Ensure user is seeing the monthly calendar view
            }
        }
    }, [targetDateStr]);

    // Edit Handling
    const handleItemClick = (item) => {
        setSelectedItem(item);
    };

    // Calendar Helper Functions
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    // --- Legend Component ---
    const Legend = () => {
        if (view === 'Timeline') {
            return (
                <div style={{ flexShrink: 0, marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1.5rem', fontSize: '0.8rem', color: '#666', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <strong style={{ color: '#333', whiteSpace: 'nowrap', marginTop: '2px' }}>Divisions (Top):</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', alignItems: 'center' }}>
                            {DIVISIONS.map(div => (
                                <div key={div.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <span style={{ width: 10, height: 10, background: getDivisionColor(div.id), borderRadius: '2px' }}></span> {div.name}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ width: '1px', height: '16px', background: '#ccc' }}></div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <strong style={{ color: '#333' }}>Priority (Bottom):</strong>
                        {['High', 'Normal', 'Low'].map(prio => (
                            <div key={prio} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <span style={{ width: 10, height: 10, background: getPriorityColor(prio), borderRadius: '2px' }}></span> {prio}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Calendar Legend
        return (
            <div style={{ flexShrink: 0, marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1.5rem', fontSize: '0.8rem', color: '#666', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                {/* Divisions */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', flex: 1 }}>
                    <strong style={{ color: '#333', whiteSpace: 'nowrap', marginTop: '2px' }}>Division (Background):</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', alignItems: 'center' }}>
                        {DIVISIONS.map(div => (
                            <div key={div.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: getDivisionColor(div.id) }}></span> {div.name}
                            </div>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }}></span> Events
                        </div>
                    </div>
                </div>

                <div style={{ width: '1px', height: '16px', background: '#ccc' }}></div>

                {/* Priorities */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <strong style={{ color: '#333' }}>Priority (Bar):</strong>
                    {['High', 'Normal', 'Low'].map(prio => (
                        <div key={prio} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{ width: 4, height: 12, background: getPriorityColor(prio), borderRadius: '1px' }}></span> {prio}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // --- Month Grid Render Logic ---
    const renderMonthGrid = () => {
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];

        // Padding
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`pad-${i}`} style={{ background: '#f9f9f9', border: '1px solid #eee', height: '150px' }}></div>);
        }

        // Actual days
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

            // Filter items for this day
            const dayItems = items.filter(item => {
                if (!item.date) return false;
                const itemDatePart = item.date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                return itemDatePart === dateStr;
            }).sort((a, b) => {
                const timeA = a.start ? a.start.getTime() : 0;
                const timeB = b.start ? b.start.getTime() : 0;
                return timeA - timeB;
            });

            const isHighlighted = activeHighlight === dateStr;
            const overflowCount = dayItems.length > 4 ? dayItems.length - 4 : 0;

            days.push(
                <div
                    key={d}
                    onClick={() => { if (isHighlighted) setActiveHighlight(null); }}
                    style={{
                        height: '150px',
                        padding: '0.3rem',
                        border: isHighlighted ? '2px solid #3b82f6' : '1px solid #eee',
                        background: isHighlighted ? '#eff6ff' : 'white',
                        cursor: isHighlighted ? 'pointer' : 'default',
                        animation: isHighlighted ? 'pulse-highlight 2s infinite' : 'none',
                        transition: 'background 0.3s ease, border 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Day Number Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.3rem',
                        flexShrink: 0,
                    }}>
                        <span style={{ fontWeight: 700, color: '#475569', fontSize: '0.82rem' }}>{d}</span>
                        {overflowCount > 0 && (
                            <span style={{
                                fontSize: '0.6rem',
                                color: '#94a3b8',
                                background: '#f1f5f9',
                                borderRadius: '4px',
                                padding: '1px 4px',
                                fontWeight: 600,
                                lineHeight: '1.4',
                            }}>
                                +{overflowCount} more
                            </span>
                        )}
                    </div>

                    {/* Scrollable Events Container */}
                    <div
                        className="calendar-cell-events"
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.2rem',
                        }}
                    >
                        {dayItems.map((item, idx) => {
                            const priorityColor = getPriorityColor(item.Priority);
                            const divColor = item.entityType === 'TASK' ? getDivisionColor(item.Division) : '#f59e0b';

                            return (
                                <div
                                    key={item.id || idx}
                                    onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                                    style={{
                                        fontSize: '0.7rem',
                                        padding: '0.25rem 0.4rem',
                                        borderRadius: '4px',
                                        color: 'white',
                                        background: divColor,
                                        borderLeft: `4px solid ${item.entityType === 'TASK' ? priorityColor : '#ffffff88'}`,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        opacity: item.status === 'COMPLETED' ? 0.7 : 1,
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                    }}
                                    title={`${item.Title} | Priority: ${item.Priority}`}
                                >
                                    {/* Status Dot for High Priority Tasks */}
                                    {item.Priority === 'High' && item.entityType === 'TASK' && (
                                        <span style={{
                                            width: 4,
                                            height: 4,
                                            background: 'white',
                                            borderRadius: '50%',
                                            marginRight: '5px',
                                            marginTop: '5px',
                                            flexShrink: 0,
                                        }}></span>
                                    )}
                                    <span style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.3',
                                    }}>
                                        {item.Title || 'Untitled'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return (
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} style={{ padding: '0.75rem', textAlign: 'center', background: '#f8fafc', fontWeight: 600, color: '#334155', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                            {day}
                        </div>
                    ))}
                    {days}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={calendarRef}
            style={isFullScreen ? {
                height: '100vh', width: '100vw', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem', overflow: 'hidden'
            } : {
                height: '100%', display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '1rem'
            }}
        >
            <style>{`
                @keyframes pulse-highlight {
                    0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
                }
                .calendar-cell-events::-webkit-scrollbar {
                    width: 5px;
                }
                .calendar-cell-events::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }
                .calendar-cell-events::-webkit-scrollbar-thumb {
                    background-color: #94a3b8;
                    border-radius: 4px;
                    border: 1px solid #f1f5f9;
                }
                .calendar-cell-events::-webkit-scrollbar-thumb:hover {
                    background-color: #64748b;
                }
            `}</style>

            {/* Unified Header & Filter Bar */}
            <div className="modern-card" style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {/* Top Row: Title & View Switcher */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem' }}>
                        <CalendarIcon size={24} className="text-primary" />
                        {view === 'Timeline' ? 'Timeline View' : 'Calendar View'}
                    </h2>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {/* View Switcher (Calendar vs Timeline) */}
                        <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                            <button
                                onClick={() => setView('Month')}
                                style={{
                                    padding: '0.4rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                    background: view === 'Month' ? 'white' : 'transparent',
                                    color: view === 'Month' ? '#0f172a' : '#64748b',
                                    fontWeight: view === 'Month' ? 600 : 500,
                                    boxShadow: view === 'Month' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Calendar
                            </button>
                            <button
                                onClick={() => setView('Timeline')}
                                style={{
                                    padding: '0.4rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                    background: view === 'Timeline' ? 'white' : 'transparent',
                                    color: view === 'Timeline' ? '#0f172a' : '#64748b',
                                    fontWeight: view === 'Timeline' ? 600 : 500,
                                    boxShadow: view === 'Timeline' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Timeline
                            </button>
                        </div>

                        <button
                            onClick={toggleFullScreen}
                            style={{
                                background: 'white', border: '1px solid #e2e8f0', padding: '0.5rem', borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b',
                                transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                            title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
                        >
                            {isFullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
                        </button>
                    </div>
                </div>

                {/* Bottom Row: Filters */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>

                    {/* 1. Division Filter (DG Only) */}
                    {(isDG) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Filter size={16} color="#64748b" />
                            <select
                                value={activeDivision}
                                onChange={(e) => setActiveDivision(e.target.value)}
                                style={{
                                    padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #e2e8f0',
                                    background: 'white', color: '#333', fontSize: '0.9rem', cursor: 'pointer'
                                }}
                            >
                                <option value="All">All Divisions</option>
                                {DIVISIONS.map(div => (
                                    <option key={div.id} value={div.id}>{div.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {view === 'Timeline' && (
                        <>
                            <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }}></div>
                            {/* 2. Timeline Scale Filter - Only visible in Timeline view */}
                            <div style={{ display: 'flex', background: '#f8fafc', padding: '2px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                {['Day', 'Week', 'Month'].map(scale => (
                                    <button
                                        key={scale}
                                        onClick={() => setTimeScale(scale)}
                                        style={{
                                            padding: '0.3rem 0.8rem', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem',
                                            background: timeScale === scale ? 'white' : 'transparent',
                                            color: timeScale === scale ? '#0f172a' : '#64748b',
                                            fontWeight: timeScale === scale ? 600 : 400,
                                            boxShadow: timeScale === scale ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                                        }}
                                    >
                                        {scale}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }}></div>

                    {/* 3. Entity Type Filter (All/Tasks/Events) */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {['All', 'Tasks', 'Events'].map(type => (
                            <button
                                key={type}
                                onClick={() => setEntityType(type)}
                                style={{
                                    padding: '0.3rem 0.8rem', borderRadius: '20px', border: `1px solid ${entityType === type ? '#3b82f6' : '#e2e8f0'}`,
                                    background: entityType === type ? '#eff6ff' : 'white',
                                    color: entityType === type ? '#1d4ed8' : '#64748b',
                                    fontSize: '0.85rem', fontWeight: entityType === type ? 600 : 400, cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    {/* Navigation (Visible in Month Calendar View) */}
                    {view === 'Month' && (
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}><ChevronLeft size={18} /></button>
                            <span style={{ fontSize: '1rem', fontWeight: 600 }}>
                                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                            </span>
                            <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}><ChevronRight size={18} /></button>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content Area */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#666' }}>Loading...</div>
                ) : (
                    <>
                        <div className="modern-card" style={{ height: '100%', overflow: 'hidden', padding: '1rem', display: 'flex', flexDirection: 'column' }}>
                            {/* Unified Legend (Top) */}
                            <Legend />

                            {view === 'Month' ? (
                                renderMonthGrid()
                            ) : (
                                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                                    <GanttChart
                                        items={items}
                                        viewMode={ganttViewMode}
                                        users={users}
                                        onTaskClick={handleItemClick}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Interaction Modals */}
            {selectedItem && (
                <ItemDetailsModal
                    item={selectedItem}
                    users={users}
                    onClose={() => setSelectedItem(null)}
                />
            )}
        </div>
    );
};

export default Calendar;